import PropTypes from "prop-types"
import React, { useContext, useEffect, useCallback } from "react"
import { useParams } from "react-router-dom"
import { Grid, Paper, TextField, Autocomplete, MenuItem } from "@mui/material"
import { useSnackbar } from "notistack"
import i18n from "i18n"

import ContactField from "components/Contacts/ContactField"
import ProjectContext from "context/projects/projectContext"
import SerieSelector from "components/ProfileRefinament/series/SerieSelector"
import { useBrands } from "components/ProfileRefinament/hooks/useBrands"
import {
  formatDateForInput,
  INVOICE_TEXT_MAX_LENGTH,
} from "../utils/invoiceUtils"

const REASON_CODES_BY_TYPE = {
  corrective: ["R1", "R4", "R5"],
  "credit-note": ["R1", "R2", "R3", "R4", "R5"],
  "debit-note": ["R1", "R4", "R5"],
}

const NO_BRAND = "__no_brand__"

const InvoiceInfo = ({
  invoiceData,
  setInvoiceData,
  hasValidationErrors,
  onInvalidContact,
  organizationId,
  isNew,
  selectedSerieSK,
  setSelectedSerieSK,
  serieType = "invoices",
  contactData = null,
}) => {
  const {
    projects = [],
    getProjects,
    loadingProjects,
  } = useContext(ProjectContext)
  const { invoiceId, projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const isProjectLocked =
    invoiceData.issued || (invoiceId === "new" && !!projectId)

  const validateClientHasCompany = useCallback(
    clientSK => {
      if (!clientSK) return
      const [uuid1, uuid2] = clientSK.split("#")
      if (uuid1 && uuid2 && uuid1 === uuid2) {
        enqueueSnackbar(i18n.t("invoices.contactNeedsCompany"), {
          variant: "warning",
        })
      }
    },
    [enqueueSnackbar],
  )

  const selectedProject =
    projects.find(p => p.projectId === invoiceData.projectId) || null

  useEffect(() => {
    if (projects.length === 0) {
      getProjects()
    }
  }, [projects, getProjects])

  useEffect(() => {
    if (projectId && projects.length > 0) {
      const found = projects.find(p => p.projectId === projectId)
      if (found) {
        setInvoiceData(prev => {
          if (prev.projectId === found.projectId && prev.contact) return prev

          if (prev.issued) {
            return {
              ...prev,
              projectId: found.projectId,
            }
          }
          const clientSK = prev.contact || found.client || ""
          if (clientSK) {
            validateClientHasCompany(clientSK)
          }
          return {
            ...prev,
            projectId: found.projectId,
            contact: clientSK,
            brandId: found.brandId ?? null,
          }
        })
      }
    }
  }, [projectId, projects, setInvoiceData, validateClientHasCompany])

  useEffect(() => {
    if (projectId && projects.length > 0 && !invoiceData.projectId) {
      const found = projects.find(p => p.projectId === projectId)
      if (found) {
        const clientSK = found.client || ""
        if (clientSK) {
          validateClientHasCompany(clientSK)
        }
        setInvoiceData(prev => {
          if (prev.issued) {
            return {
              ...prev,
              projectId: found.projectId,
            }
          }
          return {
            ...prev,
            projectId: found.projectId,
            contact: clientSK || prev.contact,
            brandId: found.brandId ?? null,
          }
        })
      }
    }
  }, [
    projectId,
    projects,
    setInvoiceData,
    invoiceData.projectId,
    validateClientHasCompany,
  ])

  const handleProjectChange = (event, newValue) => {
    const clientSK = newValue && newValue.client ? newValue.client : ""
    if (clientSK) {
      validateClientHasCompany(clientSK)
    }
    setInvoiceData(prev => {
      if (prev.issued) {
        return {
          ...prev,
          projectId: newValue ? newValue.projectId : "",
        }
      }
      return {
        ...prev,
        projectId: newValue ? newValue.projectId : "",
        contact: clientSK,
        brandId: newValue ? (newValue.brandId ?? null) : null,
      }
    })
  }
  const handleFieldChange = field => event => {
    setInvoiceData(prev => ({
      ...prev,
      [field]: event.target.value,
    }))
  }

  const handleIssueDateChange = event => {
    const newIssueDate = event.target.value
    setInvoiceData(prev => {
      const currentDue = formatDateForInput(prev.dueDate)
      const shouldAdjustDue =
        newIssueDate && currentDue && currentDue < newIssueDate
      return {
        ...prev,
        issueDate: newIssueDate,
        ...(shouldAdjustDue ? { dueDate: newIssueDate } : {}),
      }
    })
  }

  const issueDateValue = formatDateForInput(invoiceData.issueDate)

  const lockedFieldStyle = { pointerEvents: "none", background: "#f5f5f5" }
  const readOnlyStyle = isProjectLocked ? lockedFieldStyle : {}
  const datesReadOnlyStyle = invoiceData.issued ? lockedFieldStyle : {}

  const { data: brands, isLoading: isLoadingBrands } = useBrands(organizationId)
  const brandOptions = brands || []
  const assignedBrandStillExists = brandOptions.some(
    b => b.id === invoiceData.brandId,
  )
  const brandValue = assignedBrandStillExists ? invoiceData.brandId : NO_BRAND
  // An absent brand renders with the organization's default one, resolved
  // backend-side by planhopper-core libs/branding/loader.py. Naming it here
  // saves a trip to the brands settings; the generic label is kept for the
  // organization with no brands, whose documents use the account scalars.
  const defaultBrand = brandOptions.find(b => b.isDefault)
  const noBrandLabel = defaultBrand
    ? i18n.t("invoices.noBrandNamed", { name: defaultBrand.name })
    : i18n.t("invoices.noBrand")
  // With no brands, or a single one, the picker has nothing meaningful to offer:
  // it stays visible to show which branding the document will carry, but locked.
  // While the list is in flight it reads as editable, so a field that ends up
  // unlocked does not flash grey first.
  const isBrandLocked =
    invoiceData.issued || (!isLoadingBrands && brandOptions.length <= 1)
  const brandReadOnlyStyle = isBrandLocked ? lockedFieldStyle : {}

  const handleInvalidContact = useCallback(() => {
    enqueueSnackbar(i18n.t("invoices.selectClient"), { variant: "error" })
    onInvalidContact(true)
  }, [enqueueSnackbar, onInvalidContact])

  const handleContactChange = invoiceData.issued
    ? undefined
    : (_e, newValue) => {
        const clientSK = newValue ? newValue.SK : ""
        if (clientSK) {
          validateClientHasCompany(clientSK)
        }
        onInvalidContact(false)
        setInvoiceData(prev => {
          if (prev.issued) {
            return prev
          }
          return {
            ...prev,
            contact: clientSK,
          }
        })
      }

  return (
    <Paper elevation={0} sx={{ p: 3, mb: 2 }}>
      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            options={projects}
            getOptionLabel={option => option.name || ""}
            value={selectedProject}
            onChange={isProjectLocked ? undefined : handleProjectChange}
            loading={loadingProjects}
            noOptionsText={
              loadingProjects
                ? i18n.t("invoices.loadingProjects")
                : i18n.t("invoices.noProjects")
            }
            open={isProjectLocked ? false : undefined}
            disableClearable={isProjectLocked ? true : undefined}
            forcePopupIcon={isProjectLocked ? false : undefined}
            readOnly={isProjectLocked}
            slotProps={{
              textField: {
                inputProps: {
                  tabIndex: isProjectLocked ? -1 : 0,
                  style: readOnlyStyle,
                },
              },
            }}
            renderInput={params => (
              <TextField
                {...params}
                label={i18n.t("invoices.project")}
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  readOnly: isProjectLocked,
                  style: readOnlyStyle,
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          {invoiceData.issued ? (
            <TextField
              label={i18n.t("invoices.client")}
              variant="outlined"
              fullWidth
              value={
                contactData
                  ? `${contactData.firstname || ""} ${contactData.lastname || ""}`.trim()
                  : typeof invoiceData.contact === "object" &&
                      invoiceData.contact
                    ? `${invoiceData.contact.firstname || ""} ${invoiceData.contact.lastname || ""}`.trim()
                    : invoiceData.contact || ""
              }
              InputProps={{
                readOnly: true,
                style: { background: "#f5f5f5" },
              }}
            />
          ) : (
            <ContactField
              label={i18n.t("invoices.client")}
              value={invoiceData.contact || ""}
              onChange={handleContactChange}
              required
              error={
                hasValidationErrors &&
                (!invoiceData.contact || invoiceData.contact.trim() === "")
              }
              readOnly={invoiceData.issued}
              onInvalidContact={handleInvalidContact}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            select
            label={i18n.t("invoices.brand")}
            variant="outlined"
            fullWidth
            value={brandValue}
            onChange={e =>
              setInvoiceData(prev => ({
                ...prev,
                brandId: e.target.value === NO_BRAND ? null : e.target.value,
              }))
            }
            SelectProps={{ readOnly: isBrandLocked }}
            InputProps={{
              readOnly: isBrandLocked,
              style: brandReadOnlyStyle,
            }}
          >
            <MenuItem value={NO_BRAND}>{noBrandLabel}</MenuItem>
            {brandOptions.map(brand => (
              <MenuItem key={brand.id} value={brand.id}>
                {brand.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <SerieSelector
            organizationId={organizationId}
            serieType={serieType}
            mode={invoiceData.issued ? "view" : "create"}
            value={selectedSerieSK}
            onChange={setSelectedSerieSK}
            currentNumber={invoiceData.invoiceNumber}
            label={i18n.t("invoices.invoiceNumber")}
            hasError={hasValidationErrors}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label={i18n.t("invoices.issueDate")}
            variant="outlined"
            fullWidth
            type="date"
            required
            InputLabelProps={{ shrink: true }}
            value={issueDateValue}
            onChange={handleIssueDateChange}
            error={
              hasValidationErrors &&
              (!invoiceData.issueDate || issueDateValue.trim() === "")
            }
            InputProps={{
              readOnly: invoiceData.issued,
              style: datesReadOnlyStyle,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label={i18n.t("invoices.dueDate")}
            variant="outlined"
            fullWidth
            type="date"
            InputLabelProps={{ shrink: true }}
            value={formatDateForInput(invoiceData.dueDate)}
            onChange={handleFieldChange("dueDate")}
            inputProps={{
              min: issueDateValue || undefined,
            }}
            InputProps={{
              readOnly: invoiceData.issued,
              style: datesReadOnlyStyle,
            }}
          />
        </Grid>
        {(invoiceData.invoiceType === "corrective" ||
          invoiceData.invoiceType === "credit-note" ||
          invoiceData.invoiceType === "debit-note") && (
          <>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label={i18n.t("invoices.correctionReasonCode")}
                variant="outlined"
                fullWidth
                required
                value={invoiceData.correctionReasonCode || "R1"}
                onChange={handleFieldChange("correctionReasonCode")}
                error={hasValidationErrors && !invoiceData.correctionReasonCode}
                InputProps={{
                  readOnly: invoiceData.issued,
                  style: datesReadOnlyStyle,
                }}
              >
                {(REASON_CODES_BY_TYPE[invoiceData.invoiceType] || []).map(
                  code => (
                    <MenuItem key={code} value={code}>
                      {i18n.t(`invoices.correctionReasonCodes.${code}`)}
                    </MenuItem>
                  ),
                )}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label={i18n.t("invoices.reason")}
                variant="outlined"
                fullWidth
                required
                value={invoiceData.reason || ""}
                onChange={handleFieldChange("reason")}
                error={
                  hasValidationErrors &&
                  (invoiceData.reason || "").trim().length <= 5
                }
                inputProps={{
                  maxLength: INVOICE_TEXT_MAX_LENGTH,
                }}
                InputProps={{
                  readOnly: invoiceData.issued,
                  style: datesReadOnlyStyle,
                }}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Paper>
  )
}

InvoiceInfo.propTypes = {
  invoiceData: PropTypes.shape({
    projectId: PropTypes.string,
    brandId: PropTypes.string,
    invoiceNumber: PropTypes.string,
    contact: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        firstname: PropTypes.string,
        lastname: PropTypes.string,
      }),
    ]),
    company: PropTypes.string,
    issued: PropTypes.bool,
    issueDate: PropTypes.string,
    dueDate: PropTypes.string,
    paymentMethod: PropTypes.string,
    paymentStatus: PropTypes.string,
    invoiceConcepts: PropTypes.array,
    notes: PropTypes.string,
    invoiceType: PropTypes.string,
    reason: PropTypes.string,
    correctionReasonCode: PropTypes.string,
    iban: PropTypes.string,
    subtotal: PropTypes.number,
    tax: PropTypes.number,
    retention: PropTypes.number,
    total: PropTypes.number,
  }).isRequired,
  setInvoiceData: PropTypes.func.isRequired,
  hasValidationErrors: PropTypes.bool,
  onInvalidContact: PropTypes.func,
  organizationId: PropTypes.string,
  isNew: PropTypes.bool,
  selectedSerieSK: PropTypes.string,
  setSelectedSerieSK: PropTypes.func,
  contactData: PropTypes.shape({
    firstname: PropTypes.string,
    lastname: PropTypes.string,
  }),
  serieType: PropTypes.string,
}

export default InvoiceInfo
